import { useEffect, useState, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import type { Tables, Enums } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type User = Tables<'profiles'> & { organizations: { name: string } | null }

const ROLES: Enums<'user_role'>[] = ['sales_consultant', 'operations', 'installer_technician', 'marketing', 'management', 'partner_admin', 'keder_admin']

export function UsersListPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [orgFilter, setOrgFilter] = useState('')

  useEffect(() => {
    supabase.from('profiles').select('*, organizations(name)')
      .order('created_at', { ascending: false })
      .then(({ data }) => { setUsers((data as unknown as User[]) || []); setLoading(false) })
  }, [])

  const orgNames = useMemo(() => {
    const names = new Set<string>()
    for (const u of users) {
      if (u.organizations?.name) names.add(u.organizations.name)
    }
    return Array.from(names).sort()
  }, [users])

  const filtered = useMemo(() => {
    let result = users
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(u =>
        (u.full_name || '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      )
    }
    if (orgFilter) {
      result = result.filter(u => u.organizations?.name === orgFilter)
    }
    return result
  }, [users, search, orgFilter])

  const updateRole = async (userId: string, role: Enums<'user_role'>) => {
    await supabase.from('profiles').update({ role }).eq('id', userId)
    setUsers(users.map(u => u.id === userId ? { ...u, role } : u))
    setEditingId(null)
  }

  const toggleActive = async (userId: string, isActive: boolean) => {
    await supabase.from('profiles').update({ is_active: isActive }).eq('id', userId)
    setUsers(users.map(u => u.id === userId ? { ...u, is_active: isActive } : u))
  }

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading users...</p>

  return (
    <div>
      <PageHeader title="User Management" description="Manage all portal users and their roles" />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-6)', flexWrap: 'wrap' }}>
        <input
          className={css.formInput}
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 300 }}
        />
        <select
          className={css.formSelect}
          value={orgFilter}
          onChange={e => setOrgFilter(e.target.value)}
          style={{ maxWidth: 240 }}
        >
          <option value="">All Organizations</option>
          {orgNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        {(search || orgFilter) && (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', alignSelf: 'center' }}>
            {filtered.length} of {users.length} users
          </span>
        )}
      </div>

      <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
        <table className={css.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Organization</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(user => (
              <tr key={user.id}>
                <td style={{ fontWeight: 500 }}>{user.full_name || 'No name'}</td>
                <td style={{ fontSize: 'var(--text-sm)' }}>{user.email}</td>
                <td style={{ fontSize: 'var(--text-sm)' }}>{user.organizations?.name || <span style={{ color: 'var(--color-text-muted)' }}>Unassigned</span>}</td>
                <td>
                  {editingId === user.id ? (
                    <select className={css.formSelect} style={{ padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--text-xs)' }} value={user.role} onChange={e => updateRole(user.id, e.target.value as Enums<'user_role'>)}>
                      {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                    </select>
                  ) : (
                    <StatusBadge label={user.role} variant="default" />
                  )}
                </td>
                <td><StatusBadge label={user.is_active ? 'active' : 'inactive'} variant={user.is_active ? 'success' : 'error'} /></td>
                <td>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button className={css.btnGhost} style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)' }} onClick={() => setEditingId(editingId === user.id ? null : user.id)}>
                      {editingId === user.id ? 'Done' : 'Edit Role'}
                    </button>
                    <button className={css.btnGhost} style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)', color: user.is_active ? 'var(--color-error)' : 'var(--color-success)' }} onClick={() => toggleActive(user.id, !user.is_active)}>
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
